import { AssetVizMode } from '@asset-viz/defines'
import { Select, Tag } from 'antd'
import CantileverButton from 'components/CantileverButton'
import { WebGLLayer } from 'features/assetDatabase/contexts/WebGLLayer'
import { ForecastAssetViz } from 'features/assetVizAdapter'
import { DimensionsIcon } from 'features/libra/stepComponents/niiReport/cards/DimensionsIcon'
import { computeArea } from 'features/libra/stepComponents/niiReport/cards/totalModelArea'
import {
  calculateZoneArea,
  calculateZoneHeight,
  calculateZoneLength,
  createDrawingLayer,
  markupRectToZoneBounds,
  zonesToMarkupLayers,
} from 'features/libra/stepComponents/niiReport/zoneUtils'
import { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { useZoneAnalysisData } from 'features/libra/stepComponents/zoneAnalysis/ZoneAnalysisDataProvider'
import { Plus, X } from 'lucide-react'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponentShape,
  GeckoFeature,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
  GeckoMarkupRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'
import { v4 } from 'uuid'

import { ZoneRow as ZoneRowComponent } from './ZoneRow'

const AssetVizContainer = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border-radius: 8px;
  overflow: hidden;
`

const ZonesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TopControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const LeftControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const CoverageStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.forecast.background3};
  border-radius: 6px;
`

const ComponentSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ComponentLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-weight: 500;
`

const CoverageStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`

const CoverageValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const CoverageLabel = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ZonesListComponent: FC<{
  zones: ZoneParams[]
  totalAssetArea?: GeckoMeasurement
  onDeleteZone: (zoneId: string) => void
  onUpdateZone: (zoneId: string, updates: Partial<ZoneParams>) => void
}> = ({ zones, totalAssetArea, onDeleteZone, onUpdateZone }) => {
  if (zones.length === 0) {
    return (
      <ZonesList>
        <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
          No zones defined. Use &quot;Add Zone&quot; to create zones.
        </div>
      </ZonesList>
    )
  }

  return (
    <ZonesList>
      {zones.map((zone) => (
        <ZoneRowComponent
          key={zone.id}
          zone={zone}
          totalAssetArea={totalAssetArea}
          onDelete={() => onDeleteZone(zone.id)}
          onUpdate={(updates: Partial<ZoneParams>) =>
            onUpdateZone(zone.id, updates)
          }
          showCoverageBreakdown
        />
      ))}
    </ZonesList>
  )
}

export const AssetModelZoneEditor: FC = () => {
  const {
    zones,
    setZones,
    assetModelData: {
      components,
      inspections,
      assetModel,
      componentDataFrames,
    },
  } = useZoneAnalysisData()

  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [drawingLayer, setDrawingLayer] = useState<GeckoMarkupLayer>()
  const [selectedZoneId, setSelectedZoneId] = useState<string>()
  const [show3D, setShow3D] = useState(false)
  const [selection, setSelection] = useState<
    GeckoMarkupLayer | GeckoMarkupPrimitive | undefined
  >()

  // Initialize with first component
  const [selectedComponentId, setSelectedComponentId] = useState<
    string | undefined
  >(() => components[0]?.id)

  // Get inspections for the selected component
  const inspectionsForComponent = useMemo(() => {
    if (!selectedComponentId) return []

    return inspections
      .filter((insp) => insp.tags.component === selectedComponentId)
      .sort((a, b) => {
        const aTime = a.data.date?.getTime() ?? 0
        const bTime = b.data.date?.getTime() ?? 0
        return bTime - aTime // Most recent first
      })
  }, [inspections, selectedComponentId])

  // Initialize with most recent inspection for first component
  const [selectedInspectionId, setSelectedInspectionId] = useState<
    string | undefined
  >(() => {
    if (!components[0]) return undefined

    const firstComponentInspections = inspections
      .filter((insp) => insp.tags.component === components[0].id)
      .sort((a, b) => {
        const aTime = a.data.date?.getTime() ?? 0
        const bTime = b.data.date?.getTime() ?? 0
        return bTime - aTime
      })

    return firstComponentInspections[0]?.id
  })

  // Update selected inspection when component changes - always select most recent
  useEffect(() => {
    if (inspectionsForComponent.length > 0) {
      setSelectedInspectionId(inspectionsForComponent[0].id)
    }
  }, [selectedComponentId, inspectionsForComponent])

  const selectedComponent = useMemo(() => {
    const comp = components.find((c) => c.id === selectedComponentId)
    return comp ? [comp] : []
  }, [components, selectedComponentId])

  // Get component data frame for selected component and inspection
  const selectedComponentDataFrame = useMemo(() => {
    return componentDataFrames.find(
      (ds) =>
        ds.tags.component === selectedComponentId &&
        ds.tags.inspection === selectedInspectionId
    )
  }, [componentDataFrames, selectedComponentId, selectedInspectionId])

  // Get the selected inspection
  const selectedInspection = useMemo(() => {
    return inspectionsForComponent.find(
      (insp) => insp.id === selectedInspectionId
    )
  }, [inspectionsForComponent, selectedInspectionId])

  const zonesForSelectedComponent = useMemo(() => {
    if (!selectedComponentId) return []
    return zones.filter((zone) => zone.componentId === selectedComponentId)
  }, [zones, selectedComponentId])

  // Convert selected component zones to markup layers for visualization
  const zoneMarkupLayers = useMemo(() => {
    if (!selectedComponentId) return []
    return zonesToMarkupLayers(
      zonesForSelectedComponent,
      selectedComponentId,
      'org',
      'site',
      'unit'
    )
  }, [zonesForSelectedComponent, selectedComponentId])

  // Combine zone layers with drawing layer
  const allMarkupLayers = useMemo(() => {
    const layers = [...zoneMarkupLayers]
    if (drawingLayer) layers.push(drawingLayer)
    return layers
  }, [zoneMarkupLayers, drawingLayer])

  // Calculate total asset area for coverage percentages for selected component
  const totalAssetArea = useMemo(() => {
    if (!assetModel || !selectedComponentId) return undefined
    const componentModel = assetModel.data.components.get(selectedComponentId)
    return componentModel ? computeArea(componentModel) : undefined
  }, [assetModel, selectedComponentId])

  const totalCoverage = useMemo(() => {
    if (!totalAssetArea) return undefined
    const totalZoneArea = zonesForSelectedComponent.reduce(
      (sum, zone) => sum + (zone.calculated_area_sq_m || 0),
      0
    )
    const totalAssetAreaSqM = totalAssetArea.convertTo(
      GeckoMeasurementUnit.SquareMeters
    ).num
    return totalAssetAreaSqM > 0 ? (totalZoneArea / totalAssetAreaSqM) * 100 : 0
  }, [zonesForSelectedComponent, totalAssetArea])

  // Zone drawing handlers
  const handleAddZone = useCallback(() => {
    if (!selectedComponentId) return

    const measurementUnit =
      selectedComponentDataFrame?.data?.geometry?.x?.unit?.id || 'mm'

    const newDrawingLayer = createDrawingLayer(
      selectedComponentId,
      'org',
      'site',
      'unit',
      measurementUnit
    )

    setDrawingLayer(newDrawingLayer)
    setIsDrawingMode(true)
    setSelectedZoneId(undefined)
    setSelection(newDrawingLayer)
  }, [selectedComponentId, selectedComponentDataFrame])

  const handleCancelDrawing = useCallback(() => {
    setIsDrawingMode(false)
    setDrawingLayer(undefined)
    setSelection(undefined)
  }, [])

  const handleZoneDrawn = useCallback(
    (primitive: GeckoMarkupPrimitive): Promise<void> => {
      const isRect =
        primitive && primitive.constructor.name === 'GeckoMarkupRect'

      if (!isRect || !selectedComponentId) {
        return Promise.resolve()
      }

      const rect = (primitive as GeckoMarkupRect).rect

      const ZONE_COLORS = ['#FF8C00', '#3B82F6', '#22C55E', '#A855F7']
      const newZoneColor = ZONE_COLORS[zones.length % ZONE_COLORS.length]
      const newZone: ZoneParams = {
        id: v4().substring(0, 6),
        title: `Zone ${zonesForSelectedComponent.length + 1}`,
        componentId: selectedComponentId,
        bounds: markupRectToZoneBounds(primitive as GeckoMarkupRect),
        calculated_area_sq_m: calculateZoneArea(rect),
        calculated_length_mm: calculateZoneLength(rect),
        calculated_height_mm: calculateZoneHeight(rect),
        color: newZoneColor,
        geometry: 'RECTANGLE',
        damage_mechanisms: [],
        zone_recommendations: [],
        assumptions: [],
      }

      setZones((prev) => [...prev, newZone])

      setIsDrawingMode(false)
      setDrawingLayer(undefined)
      setSelectedZoneId(newZone.id)

      return Promise.resolve()
    },
    [
      selectedComponentId,
      zones.length,
      zonesForSelectedComponent.length,
      setZones,
    ]
  )

  const handleZoneClick = useCallback(
    (
      clickedSelection:
        | GeckoMarkupPrimitive
        | GeckoComponentShape
        | GeckoFeature
        | undefined
    ) => {
      if (!clickedSelection || isDrawingMode) return

      if (
        'markupLayerID' in clickedSelection &&
        clickedSelection.markupLayerID
      ) {
        const zoneId = clickedSelection.markupLayerID.replace('zone-', '')
        setSelectedZoneId(zoneId)
      }
    },
    [isDrawingMode]
  )

  const handleDeleteZone = useCallback(
    (zoneId: string) => {
      setZones((prev) => prev.filter((z) => z.id !== zoneId))

      if (selectedZoneId === zoneId) {
        setSelectedZoneId(undefined)
      }
    },
    [setZones, selectedZoneId]
  )

  const handleUpdateZone = useCallback(
    (zoneId: string, updates: Partial<ZoneParams>) => {
      setZones((prev) =>
        prev.map((z) => (z.id === zoneId ? { ...z, ...updates } : z))
      )
    },
    [setZones]
  )

  return (
    <WebGLLayer>
      <TopControls>
        <LeftControls>
          {components.length > 0 && (
            <ComponentSelector>
              <ComponentLabel>Component:</ComponentLabel>
              <Select
                style={{ minWidth: 200 }}
                value={selectedComponentId}
                onChange={setSelectedComponentId}
                placeholder='Select a component'>
                {components.map((component) => (
                  <Select.Option key={component.id} value={component.id}>
                    {component.data.name || component.id}
                  </Select.Option>
                ))}
              </Select>
            </ComponentSelector>
          )}

          {inspectionsForComponent.length > 1 && (
            <ComponentSelector>
              <ComponentLabel>Inspection:</ComponentLabel>
              <Select
                style={{ minWidth: 200 }}
                value={selectedInspectionId}
                onChange={setSelectedInspectionId}
                placeholder='Select an inspection'>
                {inspectionsForComponent.map((inspection) => (
                  <Select.Option key={inspection.id} value={inspection.id}>
                    {inspection.data.date
                      ? inspection.data.date.toLocaleDateString()
                      : inspection.id}
                  </Select.Option>
                ))}
              </Select>
            </ComponentSelector>
          )}

          <CantileverButton
            onClick={handleAddZone}
            disabled={isDrawingMode || !selectedComponentId}
            primary={!isDrawingMode}>
            <Plus size={16} style={{ marginRight: '4px' }} />
            {isDrawingMode ? 'Drawing...' : 'Add Zone'}
          </CantileverButton>

          {isDrawingMode && (
            <CantileverButton onClick={handleCancelDrawing}>
              <X size={16} style={{ marginRight: '4px' }} />
              Cancel Drawing
            </CantileverButton>
          )}

          <CantileverButton
            onClick={() => setShow3D(!show3D)}
            style={{ padding: '6px 12px' }}
            title={show3D ? 'Switch to 2D view' : 'Switch to 3D view'}>
            <DimensionsIcon show3D={show3D} />
          </CantileverButton>
        </LeftControls>

        {zonesForSelectedComponent.length > 0 &&
          totalCoverage !== undefined && (
            <CoverageStats>
              <CoverageStat>
                <CoverageValue>{totalCoverage.toFixed(1)}%</CoverageValue>
                <CoverageLabel>Total Coverage</CoverageLabel>
              </CoverageStat>
              <CoverageStat>
                <CoverageValue>
                  {zonesForSelectedComponent.length}
                </CoverageValue>
                <CoverageLabel>Zones Defined</CoverageLabel>
              </CoverageStat>
              {totalAssetArea && (
                <CoverageStat>
                  <CoverageValue>
                    {totalAssetArea
                      .convertTo(GeckoMeasurementUnit.SquareMeters)
                      .num.toFixed(1)}
                  </CoverageValue>
                  <CoverageLabel>Total Asset Area (m²)</CoverageLabel>
                </CoverageStat>
              )}
            </CoverageStats>
          )}
      </TopControls>

      {assetModel && selectedComponentId ? (
        <AssetVizContainer>
          <ForecastAssetViz
            assetModel={assetModel}
            components={selectedComponent}
            componentDataFrames={
              selectedComponentDataFrame ? [selectedComponentDataFrame] : []
            }
            selectedInspection={selectedInspection}
            markupLayers={allMarkupLayers}
            selection={selection}
            mode={
              isDrawingMode
                ? AssetVizMode.CreateMarkupRect
                : AssetVizMode.SelectBin
            }
            onWrite={handleZoneDrawn}
            onSelected={handleZoneClick}
            sceneConfig={{
              show3D: show3D,
              showFeatures: true,
              heatmapOpacity: 1,
            }}
          />
        </AssetVizContainer>
      ) : (
        <div
          style={{
            height: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#666',
          }}>
          {!assetModel
            ? 'No asset model available for visualization'
            : 'Select a component to view and define zones'}
        </div>
      )}

      <div>
        <h4 style={{ marginBottom: '12px', color: '#333' }}>
          Defined Zones
          {components.length > 1 &&
            selectedComponentId &&
            selectedComponent[0] && (
              <Tag color='blue' style={{ marginLeft: 8 }}>
                {selectedComponent[0].data.name || selectedComponentId}
              </Tag>
            )}
        </h4>
        <ZonesListComponent
          zones={zonesForSelectedComponent}
          totalAssetArea={totalAssetArea}
          onDeleteZone={handleDeleteZone}
          onUpdateZone={handleUpdateZone}
        />
      </div>
    </WebGLLayer>
  )
}
